/**
 * HowItWorksAnimation — CSS-first single-card 4-phase sequence (20s loop).
 *
 * Phase 1  0–27%  : Connect your CMS     — emerald border, plugin install view
 * Phase 2  22–52% : Content made readable — cyan, CMS API → Markdown conversion
 * Phase 3  47–77% : Delivered to AI       — blue, AI agent request + metadata
 * Phase 4  72–100%: See who's reading     — violet, dashboard metrics
 *
 * One card, four absolutely-stacked content layers that crossfade.
 * Step pills above use opacity-only animation (no CSS color property animation).
 *
 * All @keyframes in globals.css: hiw-p1/p2/p3/p4, hiw-card-border-4p,
 * hiw-beam-4p, hiw-crawler-4p, hiw-metric-bar, hiw-drift.
 */

const CYCLE = "20s";

// ─── 20 background # symbols ──────────────────────────────────────────────────
const BG_HASHES = [
  { x:  2, y: 10, size: "text-sm",   opacity: 0.15, dur: 24, delay:  0   },
  { x:  8, y: 55, size: "text-xs",   opacity: 0.11, dur: 20, delay:  6   },
  { x: 13, y: 30, size: "text-base", opacity: 0.09, dur: 28, delay:  2.5 },
  { x: 18, y: 78, size: "text-xs",   opacity: 0.13, dur: 18, delay: 14   },
  { x: 24, y: 20, size: "text-lg",   opacity: 0.07, dur: 34, delay:  3   },
  { x: 30, y: 88, size: "text-xs",   opacity: 0.14, dur: 22, delay:  8   },
  { x: 36, y: 45, size: "text-sm",   opacity: 0.10, dur: 26, delay:  1   },
  { x: 42, y: 65, size: "text-xl",   opacity: 0.05, dur: 40, delay: 10   },
  { x: 48, y: 8,  size: "text-xs",   opacity: 0.12, dur: 20, delay:  5   },
  { x: 53, y: 50, size: "text-base", opacity: 0.08, dur: 30, delay:  0.5 },
  { x: 60, y: 82, size: "text-sm",   opacity: 0.13, dur: 24, delay: 12   },
  { x: 65, y: 28, size: "text-xs",   opacity: 0.16, dur: 18, delay:  4   },
  { x: 70, y: 60, size: "text-base", opacity: 0.09, dur: 32, delay:  9   },
  { x: 76, y: 15, size: "text-sm",   opacity: 0.12, dur: 22, delay: 16   },
  { x: 80, y: 70, size: "text-lg",   opacity: 0.06, dur: 36, delay:  7   },
  { x: 85, y: 42, size: "text-xs",   opacity: 0.15, dur: 20, delay: 11   },
  { x: 90, y: 90, size: "text-sm",   opacity: 0.11, dur: 26, delay:  1.5 },
  { x: 94, y: 22, size: "text-xs",   opacity: 0.13, dur: 22, delay: 13   },
  { x: 58, y: 95, size: "text-2xl",  opacity: 0.04, dur: 48, delay:  2   },
  { x: 33, y:  3, size: "text-xs",   opacity: 0.14, dur: 18, delay: 15   },
];

// ─── Phase 1: Connect your CMS ────────────────────────────────────────────────
const CONNECT_LINES: { text: string; cls: string }[] = [
  { text: "Webflow App Store",                cls: "text-slate-400/70 font-medium mb-1" },
  { text: "✓  Legible plugin installed",      cls: "text-emerald-400/85" },
  { text: "✓  API credentials linked",        cls: "text-emerald-400/85" },
  { text: "✓  Content sync ready",            cls: "text-emerald-400/85" },
  { text: "   No code changes required",      cls: "text-slate-500/65 italic mt-1" },
  { text: "   Your site stays unchanged",     cls: "text-slate-500/65 italic" },
];

// ─── Phase 2: CMS API → Markdown ──────────────────────────────────────────────
const CONVERT_LINES_META: { label: string; value: string }[] = [
  { label: "title:",     value: '"Getting started with AI"' },
  { label: "author:",    value: '"Sarah Chen"'               },
  { label: "date:",      value: '"2024-03-14"'               },
];
const CONVERT_LINES_MD: { text: string; cls: string; glow?: boolean }[] = [
  { text: "# Getting started with AI",  cls: "text-cyan-200/85 font-semibold", glow: true },
  { text: "## What you need to know",   cls: "text-cyan-300/75" },
  { text: "- Clean, structured content",cls: "text-cyan-400/70" },
  { text: "- Rich metadata preserved",  cls: "text-cyan-400/70" },
];

// ─── Phase 3: Deliver to AI agents ────────────────────────────────────────────
const DELIVER_REQ = [
  "GET /llms.txt  HTTP/1.1",
  'User-Agent: ChatGPT-User',
];
const DELIVER_RESP: { text: string; cls: string }[] = [
  { text: "→ 200 OK  ·  Markdown + metadata",    cls: "text-blue-300/80" },
  { text: "→ author · date · source URL",        cls: "text-blue-400/65" },
  { text: "→ Your content permissions included", cls: "text-blue-400/65" },
  { text: "→ Human visitors: unchanged HTML",    cls: "text-slate-500/60 italic" },
];
const DELIVER_CRAWLERS = ["ChatGPT", "Claude", "Perplexity", "Gemini"];

// ─── Phase 4: Track AI visits ─────────────────────────────────────────────────
const TRACK_ROWS: { name: string; visits: number; tokens: string; pct: number }[] = [
  { name: "ChatGPT",    visits: 42, tokens: "8,204", pct: 100 },
  { name: "Claude",     visits: 31, tokens: "6,180", pct: 74  },
  { name: "Perplexity", visits: 18, tokens: "3,290", pct: 43  },
  { name: "Gemini",     visits:  9, tokens: "1,820", pct: 21  },
];

// ─── Pill config — opacity-only animation (no CSS color property animation) ───
const PILLS: {
  label: string;
  phaseAnim: string;
  activeCls: string;
  activeBg: string;
  activeBorder: string;
}[] = [
  {
    label: "01 Connect",
    phaseAnim: "hiw-p1",
    activeCls: "text-emerald-300/90",
    activeBg: "rgba(52,211,153,0.10)",
    activeBorder: "rgba(52,211,153,0.40)",
  },
  {
    label: "02 Convert",
    phaseAnim: "hiw-p2",
    activeCls: "text-cyan-300/90",
    activeBg: "rgba(6,182,212,0.10)",
    activeBorder: "rgba(6,182,212,0.38)",
  },
  {
    label: "03 Deliver",
    phaseAnim: "hiw-p3",
    activeCls: "text-blue-300/90",
    activeBg: "rgba(59,130,246,0.10)",
    activeBorder: "rgba(59,130,246,0.40)",
  },
  {
    label: "04 Track",
    phaseAnim: "hiw-p4",
    activeCls: "text-violet-300/90",
    activeBg: "rgba(139,92,246,0.10)",
    activeBorder: "rgba(139,92,246,0.38)",
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────
export function HowItWorksAnimation() {
  return (
    <section
      className="hash-bg-dark relative overflow-hidden pt-20 pb-10"
      aria-label="How it works — four-step pipeline"
    >
      {/* ── Animated # symbols on top of hash-bg-dark static pattern ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none" aria-hidden>
        {BG_HASHES.map((h, i) => (
          <span
            key={i}
            className={`absolute font-mono font-bold text-blue-300 ${h.size}`}
            style={{
              left: `${h.x}%`,
              top: `${h.y}%`,
              opacity: 0,
              animation: `hiw-drift ${h.dur}s ${h.delay}s infinite ease-in-out`,
            }}
          >
            #
          </span>
        ))}
      </div>

      {/* ── Hero-level header — left-aligned, matching the original hero style ── */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <p className="text-xs font-bold tracking-widest text-blue-400 uppercase mb-5">
          How it works
        </p>
        <h1 className="text-5xl md:text-6xl font-display font-extrabold text-white leading-[1.08] mb-6 max-w-3xl">
          <span className="text-blue-400">#</span> Four steps. Zero migration.
        </h1>
        <p className="text-xl text-blue-200/70 max-w-xl">
          The short version: connect your CMS, and everything else is automatic. Scroll down for the details.
        </p>
      </div>

      {/* ── Step indicator pills ── */}
      <div className="relative z-10 flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-6 px-4">
        {PILLS.map((pill, i) => (
          <div key={pill.label} className="flex items-center gap-2 sm:gap-3">
            <div className="relative">
              {/* Dim base — always visible */}
              <span
                className="inline-flex items-center px-3 py-1.5 rounded-full border font-mono text-[10px] sm:text-[11px] font-semibold tracking-wide whitespace-nowrap"
                style={{
                  borderColor: "rgba(71,85,105,0.22)",
                  background:  "rgba(30,41,59,0.20)",
                  color:       "rgba(100,116,139,0.50)",
                }}
              >
                {pill.label}
              </span>
              {/* Active overlay — only opacity animates */}
              <span
                className={`absolute inset-0 inline-flex items-center justify-center rounded-full border font-mono text-[10px] sm:text-[11px] font-semibold tracking-wide whitespace-nowrap ${pill.activeCls}`}
                style={{
                  opacity:     0,
                  background:  pill.activeBg,
                  borderColor: pill.activeBorder,
                  animation:   `${pill.phaseAnim} ${CYCLE} 0s infinite ease-in-out`,
                }}
                aria-hidden
              >
                {pill.label}
              </span>
            </div>
            {i < PILLS.length - 1 && (
              <div
                className="h-px w-4 sm:w-7 shrink-0"
                style={{ background: "rgba(71,85,105,0.22)" }}
                aria-hidden
              />
            )}
          </div>
        ))}
      </div>

      {/* ── Single card — 4 content layers crossfade ── */}
      <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6">
        <div
          className="relative rounded-xl border bg-slate-950 overflow-hidden"
          style={{
            borderColor: "rgba(52,211,153,0.35)",
            animation: `hiw-card-border-4p ${CYCLE} 0s infinite ease-in-out`,
          }}
        >
          {/* ── Chrome bar — 4 phase headers ── */}
          <div className="relative border-b border-slate-800/60 bg-slate-900/50" style={{ height: 38 }}>
            {/* 3-dot decoration (permanent) */}
            <div className="absolute inset-0 flex items-center px-4 gap-1.5 pointer-events-none" aria-hidden>
              <span className="w-1.5 h-1.5 rounded-full bg-slate-700" />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-700" />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-700" />
            </div>

            {/* Header 1: Connect */}
            <div
              className="absolute inset-0 flex items-center px-4 gap-2"
              style={{ opacity: 0, animation: `hiw-p1 ${CYCLE} 0s infinite ease-in-out` }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/70 ml-9" />
              <span className="font-mono text-[9px] text-emerald-400/60 uppercase tracking-wider">
                cms marketplace
              </span>
              <span className="ml-auto font-mono text-[9px] text-slate-600 font-bold">01 CONNECT</span>
            </div>

            {/* Header 2: Convert */}
            <div
              className="absolute inset-0 flex items-center px-4 gap-2"
              style={{ opacity: 0, animation: `hiw-p2 ${CYCLE} 0s infinite ease-in-out` }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-500/60 ml-9" />
              <span className="font-mono text-[9px] text-cyan-400/55 uppercase tracking-wider">
                cms api → markdown
              </span>
              <span className="ml-auto font-mono text-[9px] text-slate-600 font-bold">02 CONVERT</span>
            </div>

            {/* Header 3: Deliver */}
            <div
              className="absolute inset-0 flex items-center px-4 gap-2"
              style={{ opacity: 0, animation: `hiw-p3 ${CYCLE} 0s infinite ease-in-out` }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full bg-blue-500/70 ml-9"
                style={{ boxShadow: "0 0 4px rgba(96,165,250,0.5)" }}
              />
              <span className="font-mono text-[9px] text-blue-400/55 uppercase tracking-wider">
                ai agent request
              </span>
              <span className="ml-auto font-mono text-[9px] text-slate-600 font-bold">03 DELIVER</span>
            </div>

            {/* Header 4: Track */}
            <div
              className="absolute inset-0 flex items-center px-4 gap-2"
              style={{ opacity: 0, animation: `hiw-p4 ${CYCLE} 0s infinite ease-in-out` }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full bg-violet-500/70 ml-9"
                style={{ boxShadow: "0 0 4px rgba(139,92,246,0.4)" }}
              />
              <span className="font-mono text-[9px] text-violet-400/55 uppercase tracking-wider">
                ai dashboard
              </span>
              <span className="ml-auto font-mono text-[9px] text-slate-600 font-bold">04 TRACK</span>
            </div>
          </div>

          {/* ── Content area — fixed height, 4 layers ── */}
          <div className="relative" style={{ minHeight: 242 }}>

            {/* Scanner beam — sweeps during phase 2 */}
            <div
              className="absolute inset-y-0 w-16 pointer-events-none"
              style={{
                background: "linear-gradient(90deg, transparent, rgba(6,182,212,0.16) 40%, rgba(165,243,252,0.10) 60%, transparent)",
                boxShadow: "0 0 20px 4px rgba(6,182,212,0.04)",
                opacity: 0,
                animation: `hiw-beam-4p ${CYCLE} 0s infinite linear`,
                zIndex: 10,
              }}
            />

            {/* ── Layer 1: Connect your CMS ── */}
            <div
              className="absolute inset-0 px-5 py-4"
              style={{ opacity: 0, animation: `hiw-p1 ${CYCLE} 0s infinite ease-in-out` }}
            >
              <div className="space-y-[5px]">
                {CONNECT_LINES.map((line, i) => (
                  <div
                    key={i}
                    className={`font-mono text-[11px] leading-snug ${line.cls}`}
                  >
                    {line.text}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Layer 2: CMS API → Markdown ── */}
            <div
              className="absolute inset-0 px-5 py-4"
              style={{ opacity: 0, animation: `hiw-p2 ${CYCLE} 0s infinite ease-in-out` }}
            >
              {/* Metadata block */}
              <div className="mb-3 space-y-[3px]">
                {CONVERT_LINES_META.map((m, i) => (
                  <div key={i} className="font-mono text-[10.5px] leading-snug">
                    <span className="text-slate-500/70">{m.label} </span>
                    <span className="text-cyan-300/65">{m.value}</span>
                  </div>
                ))}
              </div>
              {/* Separator */}
              <div className="h-px mb-3" style={{ background: "rgba(71,85,105,0.30)" }} />
              {/* Markdown output */}
              <div className="space-y-[4px]">
                {CONVERT_LINES_MD.map((line, i) => (
                  <div
                    key={i}
                    className={`font-mono text-[10.5px] leading-snug ${line.cls}`}
                    style={line.glow ? { textShadow: "0 0 10px rgba(6,182,212,0.35)" } : undefined}
                  >
                    {line.text}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Layer 3: Deliver to AI agents ── */}
            <div
              className="absolute inset-0 px-5 py-4"
              style={{ opacity: 0, animation: `hiw-p3 ${CYCLE} 0s infinite ease-in-out` }}
            >
              {/* Incoming request */}
              <div className="mb-3 space-y-[3px]">
                {DELIVER_REQ.map((line, i) => (
                  <div key={i} className="font-mono text-[10.5px] text-slate-500/65 leading-snug">
                    {line}
                  </div>
                ))}
              </div>
              {/* Response lines */}
              <div className="space-y-[5px]">
                {DELIVER_RESP.map((line, i) => (
                  <div key={i} className={`font-mono text-[10.5px] leading-snug ${line.cls}`}>
                    {line.text}
                  </div>
                ))}
              </div>
              {/* AI crawler badges — pulse in during phase 3 */}
              <div
                className="absolute bottom-0 left-0 right-0 px-5 py-2.5 border-t border-slate-800/50 flex flex-wrap gap-1.5"
                style={{
                  opacity: 0,
                  animation: `hiw-crawler-4p ${CYCLE} 0s infinite ease-out`,
                }}
              >
                {DELIVER_CRAWLERS.map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-blue-500/20 bg-blue-500/5 font-mono text-[9px] text-blue-300/75"
                  >
                    <span
                      className="w-1 h-1 rounded-full bg-blue-400/80"
                      style={{ boxShadow: "0 0 3px rgba(96,165,250,0.6)" }}
                    />
                    {name}
                  </span>
                ))}
              </div>
            </div>

            {/* ── Layer 4: AI Dashboard ── */}
            <div
              className="absolute inset-0 px-5 py-4"
              style={{ opacity: 0, animation: `hiw-p4 ${CYCLE} 0s infinite ease-in-out` }}
            >
              <div className="mb-2">
                <span className="font-mono text-[9px] text-violet-400/50 uppercase tracking-wider">
                  AI visibility · last 30 days
                </span>
              </div>
              <div className="space-y-[9px]">
                {TRACK_ROWS.map((row) => (
                  <div key={row.name}>
                    <div className="flex items-center justify-between mb-[3px]">
                      <span className="font-mono text-[10px] text-slate-300/75 font-medium">{row.name}</span>
                      <span className="font-mono text-[9px] text-slate-500/65">
                        {row.visits} visits · {row.tokens} tokens
                      </span>
                    </div>
                    <div className="h-[5px] rounded-full overflow-hidden" style={{ background: "rgba(71,85,105,0.20)" }}>
                      <div
                        className="h-full rounded-full origin-left"
                        style={{
                          width: `${row.pct}%`,
                          background: "linear-gradient(90deg, rgba(139,92,246,0.7), rgba(96,165,250,0.5))",
                          opacity: 0,
                          transform: "scaleX(0)",
                          animation: `hiw-metric-bar ${CYCLE} 0s infinite ease-out`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-slate-800/50">
                <span className="font-mono text-[10px] text-violet-300/60">
                  ↑ 28% AI visibility this month
                </span>
              </div>
            </div>
          </div>

          {/* ── Footer: phase label ── */}
          <div className="relative border-t border-slate-800/50 bg-slate-900/30" style={{ height: 30 }}>
            {[
              { anim: "hiw-p1", text: "connect — cms marketplace",      cls: "text-emerald-500/40" },
              { anim: "hiw-p2", text: "convert — cms api → markdown",    cls: "text-cyan-500/40"    },
              { anim: "hiw-p3", text: "deliver — ai-readable output",    cls: "text-blue-400/40"    },
              { anim: "hiw-p4", text: "track — ai visibility dashboard", cls: "text-violet-400/40"  },
            ].map(({ anim, text, cls }) => (
              <div
                key={anim}
                className="absolute inset-0 flex items-center px-4"
                style={{ opacity: 0, animation: `${anim} ${CYCLE} 0s infinite ease-in-out` }}
              >
                <span className={`font-mono text-[9px] font-bold uppercase tracking-widest ${cls}`}>
                  {text}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default HowItWorksAnimation;
